// React and third-party libraries
import * as React from "react";
import styled from "styled-components";
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from "lucide-react";

// Local imports (styles, constants, etc.)
import { colors, media } from "../../../style/constants";
import { h3 } from "../../../style/text";
import scrollToSection from "../../../utils/scrollToSection";

// Components imports
import Button from "../../../components/ui/Button";

// Translation
import { useTranslation } from "react-i18next";
import { GatsbyImage } from "gatsby-plugin-image";

/* Styled Components */

// Section wrapper with full-screen height and subtle background grid
const SectionWrapper = styled.section`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100vh;
`;

// Container for hero text content with vertical centering
const HeroTextContent = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  height: 100%;
  z-index: 12;

  @media (min-width: ${media.tabletSm}px) {
    padding-left: 3rem;
    padding-right: 3rem;
  }
`;

// Floating indicator at bottom center to suggest scroll
const ArrowDownIndicator = styled(motion.div)`
  position: absolute;
  right: 0;
  bottom: 40px;
  left: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// Wrapper for the downward chevron icon
const Icon = styled.div`
  transition: transform 0.2s ease-in-out;
`;

// Main title style
const HeroTitle = styled(motion.h3)`
  margin-bottom: 0.5rem;
  text-align: center;
  ${h3}

  @media (min-width: ${media.tabletSm}px) {
    margin-bottom: 1rem;
  }
`;

// Subtitle style with responsive sizing
const HeroSubtitle = styled(motion.p)`
  text-align: left;
  font-size: 1.125rem;
  line-height: 1.75rem;
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (min-width: ${media.tabletSm}px) {
    margin-bottom: 2rem;
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
`;

// Overlay for darkening the background image
const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  background-image: linear-gradient(to bottom, transparent 75%, ${colors.background});
`;



/**
 * HeroSection Component - Products Page
 *
 * @param {string} title - Main heading for the section
 * @param {string} subtitle - Supporting text beneath the title
 */
const HeroSection = ({ title, subtitle, imageData }) => {
  const { t } = useTranslation(); // Explicitly specify the namespace

  // Reference for the section, used to track scroll position
  const sectionRef = React.useRef(null);

  // Framer Motion scroll tracking for animations
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  // Animate opacity based on scroll
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Animate vertical movement based on scroll
  const translateY = useTransform(scrollYProgress, [0, 0.5], [0, 150]);

  return (
    <SectionWrapper ref={sectionRef}>
      <Overlay /> {/* Overlay for darkening the background image */}
      {imageData && (
        <GatsbyImage
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundPosition: "center center",
            filter: "brightness(0.3) blur(4px)",
          }}
          image={imageData}
          alt={title}
        />
      )}
      <HeroTextContent style={{ opacity, y: translateY }}>
        <HeroTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {title}
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {subtitle}
        </HeroSubtitle>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Button onClick={() => scrollToSection("hardwareSection")}>
            { t("heroSection.tag") }
          </Button>
        </motion.div>
      </HeroTextContent>

      {/* Animated scroll indicator */}
      <ArrowDownIndicator
        animate={{ y: [0, -20, 0] }}
        transition={{
          duration: 1,
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'loop',
        }}
        onClick={() => scrollToSection("hardwareSection")}
      >
        <Icon>
          <ChevronDown color={colors.white} size={50} />
        </Icon>
      </ArrowDownIndicator>
    </SectionWrapper>
  );
};

export default HeroSection;
